import { createContext, useContext, useMemo } from 'react';
import { useCurrentView } from '../../../views/hooks/useCurrentView';
import { useColumnWidths } from '../../../rewrite/columnWidths/hooks/useColumnWidths';
const emptyStringArray = [];
const emptyColumnsArray = [];
import { getFrameworkDataSchemasClient } from 'framework-data-schema-resolvers';
import { isAssociationColumn, parseAssociationIdFromColumnName } from '../../../associations/utils/associationIdUtils';
import { getSupportedAssociationsByObjectType } from 'crm-index-associations-lib/utils/getSupportedAssociationsByObjectType';
import { Metrics } from '../../../lib/metrics/metrics';
import Raven from 'raven-js';
import { registerQuery, useQuery } from 'data-fetching-client';
import { useSelectedObjectTypeId } from '../../../objectTypeIdContext/hooks/useSelectedObjectTypeId';
import { useHasAllGates } from '../../../auth/hooks/useHasAllGates';
import { gate } from 'hub-http/gates';
import { useCurrentPageType } from '../../../views/hooks/useCurrentPageType';
import { useColumnWidthsStatus } from '../../../rewrite/columnWidths/hooks/useColumnWidthsStatus';
import { jsx as _jsx } from "react/jsx-runtime";
const convertFDSRAssociationToIndexAssociationLibAssociation = fdsrAssociation => {
  return {
    associationCategory: fdsrAssociation.category,
    associationTypeId: fdsrAssociation.id,
    fromObjectTypeDefinition: {
      objectTypeId: fdsrAssociation.fromObjectTypeId
      //this is unavoidable unless we rewrite crm-index-associations-lib - it expects a full objectTypeDefinition which we dont have, but it also doesnt use it at runtime
    },
    toObjectTypeDefinition: {
      objectTypeId: fdsrAssociation.toObjectTypeId
      //this is unavoidable unless we rewrite crm-index-associations-lib - it expects a full objectTypeDefinition which we dont have, but it also doesnt use it at runtime
    },
    cardinality: String(fdsrAssociation.cardinality),
    fromObjectTypeId: fdsrAssociation.fromObjectTypeId,
    toObjectTypeId: fdsrAssociation.toObjectTypeId
  };
};
const fetchSupportedAssociationIds = ({
  objectTypeId
}) => {
  return getFrameworkDataSchemasClient().then(client => {
    return client.associations.get({
      frameworkTypeIdentifier: objectTypeId
    });
  }).then(associationDefinitionsArray => {
    const associationDefinitionsAdapted = associationDefinitionsArray.map(def => {
      return Object.assign({}, def, {
        toObjectTypeDefinition: {
          objectTypeId: def.toObjectTypeId
        },
        fromObjectTypeDefinition: {
          objectTypeId: def.fromObjectTypeId
        },
        associationCategory: def.category,
        associationTypeId: def.id
      });
    });
    const entries = Object.entries(associationDefinitionsAdapted).reduce((accumulator, [associationId, assodicationDefinition]) => {
      accumulator[associationId] = convertFDSRAssociationToIndexAssociationLibAssociation(assodicationDefinition);
      return accumulator;
    }, {});
    const supportedAssociationIds = new Set(getSupportedAssociationsByObjectType(objectTypeId, entries));
    return supportedAssociationIds;
  }).catch(e => {
    Raven.captureException(e);
  });
};
const supportedAssociationIdsQuery = registerQuery({
  fieldName: 'supportedAssociationIdsQuery',
  args: ['objectTypeId'],
  fetcher: fetchSupportedAssociationIds
});
const useFetchSupportedAssociationIds = ({
  objectTypeId,
  skip
}) => {
  const {
    data,
    loading,
    error
  } = useQuery(supportedAssociationIdsQuery, {
    variables: {
      objectTypeId
    },
    skip
  });
  return useMemo(() => ({
    data: data === null || data === void 0 ? void 0 : data.supportedAssociationIdsQuery,
    loading,
    error
  }), [data, loading, error]);
};
const TableColumnValidationContext = /*#__PURE__*/createContext(null);
const useIsUngatedForColumnValidation = () => {
  return useHasAllGates()(gate('CRM:Grid:MoveAssociationFilteringIntoCrmIndexUI'));
};
export const ProvideTableColumnValidation = ({
  children
}) => {
  const pageType = useCurrentPageType();
  const objectTypeId = useSelectedObjectTypeId();
  const isUngated = useIsUngatedForColumnValidation();
  const value = useFetchSupportedAssociationIds({
    objectTypeId,
    skip: !isUngated || pageType !== 'list'
  });
  if (!isUngated) {
    return children;
  }
  return /*#__PURE__*/_jsx(TableColumnValidationContext.Provider, {
    value: value,
    children: children
  });
};
const useTableColumnValidationContext = () => {
  const value = useContext(TableColumnValidationContext);
  const isUngated = useIsUngatedForColumnValidation();
  if (!isUngated) {
    return null;
  }
  if (!value) {
    throw new Error('useTableColumnValidationContext must be used within a ProvideTableColumnValidation');
  }
  return value;
};
const useValidateTableColumns = ({
  columnPropertyNames
}) => {
  const tableColumnValidationContextValue = useTableColumnValidationContext();
  return useMemo(() => {
    if (tableColumnValidationContextValue === null) {
      //this is the gated scenario
      return {
        data: columnPropertyNames,
        loading: false,
        error: undefined
      };
    }
    const {
      data,
      loading,
      error
    } = tableColumnValidationContextValue;
    const filteredData = columnPropertyNames.filter(columnName => {
      const associationId = parseAssociationIdFromColumnName(columnName);
      if (isAssociationColumn({
        name: columnName
      }) && data && associationId) {
        const hasAssociation = data.has(associationId);
        if (!hasAssociation) {
          Metrics.counter('MISSING_ASSOCIATION_IN_ASSOCIATION_DEFINITIONS', {
            associationId: associationId || ''
          }).increment();
        }
        return hasAssociation;
      }
      return true;
    });
    return {
      data: filteredData,
      loading,
      error
    };
  }, [columnPropertyNames, tableColumnValidationContextValue]);
};
export const useTableColumns = () => {
  const currentView = useCurrentView();
  const columnWidthsStatus = useColumnWidthsStatus();
  const isColumnWidthsLoading = columnWidthsStatus === 'UNINITIALIZED' || columnWidthsStatus === 'PENDING';
  const widths = useColumnWidths();
  const unfilteredColumnNames = useMemo(() => (currentView === null || currentView === void 0 ? void 0 : currentView.columns.map(columnConfig => columnConfig.name)) || emptyStringArray, [currentView === null || currentView === void 0 ? void 0 : currentView.columns]);
  const {
    data: filteredColumnNames,
    loading,
    error
  } = useValidateTableColumns({
    columnPropertyNames: unfilteredColumnNames
  });
  return useMemo(() => {
    var _filteredColumnNames$;
    const filteredData = (_filteredColumnNames$ = filteredColumnNames.map(name => ({
      name,
      width: widths[name]
    }))) !== null && _filteredColumnNames$ !== void 0 ? _filteredColumnNames$ : emptyColumnsArray;
    return {
      data: {
        columns: filteredData,
        columnPropertyNames: filteredColumnNames
      },
      loading: loading || isColumnWidthsLoading,
      error
    };
  }, [error, filteredColumnNames, isColumnWidthsLoading, loading, widths]);
};